import React from 'react';
import { BellRingIcon, CheckIcon } from 'lucide-react';
import { Button } from './Button';
import { FounderNote } from './FounderNote';
import { images } from '../data/content';
import type { HeroCopy } from '../data/heroVersions';
import type { LandingActions } from '../types/actions';
interface HeroProps extends Pick<LandingActions, 'onFormCta'> {
  copy: HeroCopy;
}
export function Hero({
  copy,
  onFormCta
}: HeroProps) {
  return <section id="top" className="w-full bg-white" data-id="element-150" data-mp="128yye9fizpx2">
      <div className="mx-auto grid max-w-content gap-10 px-4 pb-14 pt-7 md:px-8 md:pb-20 md:pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 lg:pt-16" data-id="element-151" data-mp="xanqi48sjphh">
        <div data-id="element-152" data-mp="sde3ev8vjpwt">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent md:text-xs" data-id="element-153" data-mp="sd47ys528qjq">
            {copy.eyebrow}
          </p>

          <h1 className="mt-3 font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[40px] lg:text-[52px]" data-id="element-154" data-mp="3d2l9jc5qb50p">
            {copy.headline.map((line, i) => <span key={line} className={`block ${i === copy.headline.length - 1 ? 'text-accent' : ''}`} data-id="element-155" data-mp="3j893p1w0vrk9" data-mpi={i}>
                {line}
              </span>)}
          </h1>

          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-600 md:mt-6 md:text-[17px]" data-id="element-156" data-mp="39al7s8moke0i">
            {copy.intro}
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center md:mt-8" data-id="element-157" data-mp="3e7uydrlt2vpx">
            <Button onClick={() => onFormCta('hero_primary')} fullWidth className="sm:w-auto" data-id="element-158" data-mp="30h9crv4yw7oc">
              {copy.primaryCta}
            </Button>
            <Button variant="outline" onClick={() => onFormCta('hero_secondary')} fullWidth className="sm:w-auto" data-id="element-159" data-mp="2ql6dqy5ewp5m">
              {copy.secondaryCta}
            </Button>
          </div>

          <ul className="mt-5 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-2" data-id="element-160" data-mp="2hk85g3nxxvh8">
            {copy.microProof.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-ink-400" data-id="element-161" data-mp="s1rqqjt21ys6">
                <CheckIcon className="mt-[2px] h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" data-id="element-162" data-mp="nbl3anaa4kbi" />
                {item}
              </li>)}
          </ul>
        </div>

        <div className="relative" data-id="element-163" data-mp="dd5dcnmznh3l">
          <div className="overflow-hidden rounded-2xl bg-canvas" data-id="element-164" data-mp="de6o0r03e1ks">
            <img src={images.hero} alt="Arizona real estate agent standing outside a modern desert home at sunset" width={1680} height={945} loading="eager" decoding="async" style={{
            objectPosition: '74% 22%'
          }} className="h-[320px] w-full object-cover sm:h-[420px] lg:h-[560px]" data-id="element-165" data-mp="1b0w05toidukd" />
          </div>

          {/* Subtle workflow overlay: agent empowerment plus technology */}
          <div className="absolute -bottom-5 left-3 w-[86%] max-w-[340px] rounded-xl border border-line bg-white p-3.5 shadow-[0_18px_40px_-24px_rgba(11,26,43,0.45)] sm:left-6" data-id="element-166" data-mp="3hrdbj1m0nm32">
            <div className="flex items-center gap-2" data-id="element-167" data-mp="1x786nshfdzw8">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-soft" data-id="element-168" data-mp="21bxyn68yqubv">
                <BellRingIcon className="h-3.5 w-3.5 text-accent" aria-hidden="true" data-id="element-169" data-mp="268y4bg1whv2g" />
              </span>
              <p className="text-[13px] font-semibold text-ink" data-id="element-170" data-mp="2b1hiae4i2s7s">Next best action</p>
              <span className="ml-auto text-[11px] font-medium text-ink-400" data-id="element-171" data-mp="2g0luldmq162c">Auto-assigned</span>
            </div>
            <p className="mt-2.5 text-[13px] leading-snug text-ink-600" data-id="element-172" data-mp="2kxkivfjpeism">
              New Scottsdale inquiry routed to you. First text sent, call reminder set for 4:15 PM.
            </p>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-line" data-id="element-173" data-mp="2tyiscdcwviil">
              <div className="h-full w-[72%] rounded-full bg-accent" data-id="element-174" data-mp="un98lk4twddk" />
            </div>
          </div>
        </div>
      </div>

      <FounderNote data-id="element-175" data-mp="zm2o1i3oeegd" />
    </section>;
}